import { requestJson, responseRecord } from '../api/http';

export type OfferInterval = 'month';

/** The only offer data retained by the browser between auth steps. */
export interface PendingOffer {
  id: string;
  interval: OfferInterval;
}

/** The W02 public projection: no grants, capacity, or price refs. */
export interface OfferPreview {
  id: string;
  name: string;
  price: number | null;
  /** ISO currency of `price` (minor units); null when the server did not send one. */
  currency: string | null;
  billing_period: string | null;
}

const PENDING_OFFER_KEY = 'auteur_cloud.signup.pending_offer';
const OFFER_PREVIEW_PATH = '/v1/billing/offer-preview?offer=';

/**
 * How long a selected offer survives between signup and checkout. The
 * verification email link normally opens in a NEW tab, so the value has to
 * outlive the tab that made it; the short expiry bounds how long a forgotten
 * choice can linger in a shared browser.
 */
export const PENDING_OFFER_TTL_MS = 2 * 60 * 60 * 1000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validId(value: unknown): value is string {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 160 &&
    value.trim() === value &&
    !/[\\\u0000-\u001f\u007f]/.test(value);
}

function normalizePendingOffer(value: unknown): PendingOffer | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value).sort();
  if (keys.length !== 2 || keys[0] !== 'id' || keys[1] !== 'interval') return null;
  if (!validId(value.id) || value.interval !== 'month') return null;
  return { id: value.id, interval: 'month' };
}

/**
 * The stored envelope is `{ offer: { id, interval }, expires_at }` and nothing
 * else. An expiry that is past, or further out than the TTL allows, means the
 * entry was not written by `setPendingOffer` and is discarded.
 */
function readEnvelope(value: unknown, now: number): PendingOffer | null {
  if (!isRecord(value)) return null;
  const keys = Object.keys(value).sort();
  if (keys.length !== 2 || keys[0] !== 'expires_at' || keys[1] !== 'offer') return null;
  const expiresAt = value.expires_at;
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return null;
  if (expiresAt <= now || expiresAt > now + PENDING_OFFER_TTL_MS) return null;
  return normalizePendingOffer(value.offer);
}

export function getPendingOffer(now: number = Date.now()): PendingOffer | null {
  try {
    const raw = window.localStorage.getItem(PENDING_OFFER_KEY);
    if (!raw) return null;
    const pending = readEnvelope(JSON.parse(raw), now);
    if (!pending) clearPendingOffer();
    return pending;
  } catch {
    clearPendingOffer();
    return null;
  }
}

/** Save only a validated catalog id and supported interval, with an expiry. */
export function setPendingOffer(value: unknown, now: number = Date.now()): boolean {
  const pending = normalizePendingOffer(value);
  if (!pending) {
    clearPendingOffer();
    return false;
  }
  try {
    window.localStorage.setItem(
      PENDING_OFFER_KEY,
      JSON.stringify({ offer: pending, expires_at: now + PENDING_OFFER_TTL_MS }),
    );
    return true;
  } catch {
    return false;
  }
}

export function clearPendingOffer(): void {
  try {
    window.localStorage.removeItem(PENDING_OFFER_KEY);
  } catch {
    // Storage may be unavailable in restricted browser contexts.
  }
}

/**
 * Resolve one offer through W02's public allowlisted projection. A caller must
 * repeat this check before checkout; the stored value is never price
 * authority.
 */
export async function fetchOfferPreview(offerId: string): Promise<OfferPreview> {
  if (!validId(offerId)) throw new Error('the selected offer is invalid');
  const payload = responseRecord(await requestJson(
    OFFER_PREVIEW_PATH + encodeURIComponent(offerId),
    { method: 'GET' },
  ));
  const id = payload.id;
  const name = payload.name;
  const price = payload.price;
  const billingPeriod = payload.billing_period;
  // Older servers omit `currency`; that is not malformed, the price is then
  // shown without a currency guess.
  const currency = payload.currency === undefined ? null : payload.currency;
  if (
    id !== offerId ||
    typeof name !== 'string' ||
    !name.trim() ||
    !(price === null || (typeof price === 'number' && Number.isInteger(price) && price >= 0)) ||
    !(billingPeriod === null || typeof billingPeriod === 'string') ||
    !(currency === null || (typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency)))
  ) {
    throw new Error('the offer preview response is malformed');
  }
  return {
    id,
    name,
    price,
    currency,
    billing_period: billingPeriod,
  };
}

/** Re-read the narrow public projection immediately before the checkout UI uses it. */
export async function resolvePendingOffer(): Promise<{ pending: PendingOffer; preview: OfferPreview } | null> {
  const pending = getPendingOffer();
  if (!pending) return null;
  try {
    const preview = await fetchOfferPreview(pending.id);
    if (preview.billing_period && preview.billing_period !== pending.interval) {
      throw new Error('the selected billing interval is no longer available');
    }
    return { pending, preview };
  } catch (failure) {
    // An id the server does not recognise (or a changed interval) must not
    // linger in storage for the next visit.
    clearPendingOffer();
    throw failure;
  }
}
